import { uploadSellerImage } from "../../lib/imageUploads";
import ResponsiveImage from "../../components/common/ResponsiveImage";
import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  ImagePlus,
  Images,
  Trash2,
  Pencil,
  X,
  Hash,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import ShopImageCropper from "../../components/portal/ShopImageCropper";
import { IMAGE_ACCEPT, IMAGE_ACCEPT_LIST } from "../../lib/pickImage";
import {
  containsEmailAddress,
  containsPhoneNumber,
  containsWebsite,
} from "../../lib/shopTextSafety";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAPTION_MAX = 500;
const MAX_IMAGES_PER_POST = 10;
const IMAGE_MIN = { width: 320, height: 170 };
const IMAGE_MAX_BYTES = 20 * 1024 * 1024;
const MIN_ASPECT_RATIO = 0.8;
const MAX_ASPECT_RATIO = 1.91;
const ASPECT_PRESETS = [
  {
    key: "square",
    get label() { return tr("Square"); },
    display: "1:1",
    value: 1,
    outputSize: { width: 1080, height: 1080 },
  },
  {
    key: "portrait",
    get label() { return tr("Portrait"); },
    display: "4:5",
    value: 4 / 5,
    outputSize: { width: 1080, height: 1350 },
  },
  {
    key: "landscape",
    get label() { return tr("Landscape"); },
    display: "1.91:1",
    value: 1.91,
    outputSize: { width: 1080, height: 565 },
  },
];

const DEFAULT_ASPECT_KEY = "square";

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImageDimensions = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });

const formatMb = (bytes) => Math.round(bytes / (1024 * 1024));

const captionContactError = (value = "") => {
  if (containsEmailAddress(value)) {
    return tr("Captions can't include email addresses.");
  }
  if (containsPhoneNumber(value)) {
    return tr("Captions can't include phone numbers.");
  }
  if (containsWebsite(value)) {
    return tr("Captions can't include websites.");
  }
  return "";
};

const getAspectPreset = (key) =>
  ASPECT_PRESETS.find((preset) => preset.key === key) || ASPECT_PRESETS[0];

const getPostImages = (post) =>
  Array.isArray(post?.images) && post.images.length
    ? post.images
    : [post?.image].filter(Boolean);

const getPostImageCount = (post) =>
  Math.max(Number(post?.image_count) || 0, getPostImages(post).length);

/* ---------- caption rendering with hashtag highlight ---------- */
const renderCaption = (text) => {
  if (!text) return null;
  const parts = text.split(/(\s+)/);
  return parts.map((part, i) => {
    if (/^#[A-Za-z0-9_]+$/.test(part)) {
      return (
        <span key={i} className="font-semibold text-[#2476FF]">
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const extractHashtags = (text) => {
  const matches = (text || "").match(/#[A-Za-z0-9_]+/g) || [];
  return matches;
};

/* ---------- Caption textarea with counter + hashtag footer ---------- */
const CaptionField = ({ value, onChange, error = "", testidPrefix = "" }) => {
  usePartnerTranslation();
  const tags = useMemo(() => extractHashtags(value), [value]);
  const len = (value || "").length;
  const nearLimit = len >= CAPTION_MAX * 0.9;
  const over = len > CAPTION_MAX;
  return (
    <div>
      <textarea
        data-testid={`${testidPrefix}caption-input`}
        rows={4}
        value={value}
        maxLength={CAPTION_MAX}
        onChange={(e) => onChange(e.target.value)}
        placeholder={tr("Write a caption… add hashtags like #rome or #walkingtour")}
        className={cn(
          "humrahe-input resize-none py-3",
          error && "border-[#9B0000] focus:border-[#9B0000]",
        )}
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        {error ? (
          <span className="text-xs font-semibold text-[#9B0000]">{error}</span>
        ) : tags.length > 0 ? (
          <div
            data-testid={`${testidPrefix}hashtags-preview`}
            className="flex flex-wrap items-center gap-1.5 text-xs text-[#6B7280]"
          >
            <Hash className="h-3.5 w-3.5" strokeWidth={1.8} />
            {tags.slice(0, 8).map((t, i) => (
              <span
                key={`${t}-${i}`}
                className="rounded-full bg-[#EAF1FF] px-2 py-0.5 font-semibold text-[#2476FF]"
              >
                {t}
              </span>
            ))}
            {tags.length > 8 && (
              <span className="text-[#9BA1AB]">+{tags.length - 8}{tr(" more")}</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-[#9BA1AB]">{tr("Tip: tags like")}{" "}
            <span className="font-semibold text-[#2476FF]">#rome</span>,{" "}
            <span className="font-semibold text-[#2476FF]">#walkingTour</span>{" "}
            <span className="font-semibold text-[#2476FF]">#londonMusic</span>{" "}{tr("help people discover you.")}</span>
        )}
        <span
          data-testid={`${testidPrefix}caption-counter`}
          className={cn(
            "text-xs tabular-nums",
            over ? "text-[#9B0000]" : nearLimit ? "text-[#B76A00]" : "text-[#9BA1AB]",
          )}
        >
          {len}/{CAPTION_MAX}
        </span>
      </div>
    </div>
  );
};

/* ---------- Upload composer card ---------- */
const UploadComposer = ({ onPosted }) => {
  usePartnerTranslation();
  const [cropper, setCropper] = useState({ open: false, index: 0, recrop: false });
  const [sourceImages, setSourceImages] = useState([]);
  const [croppedImages, setCroppedImages] = useState([]);
  const [aspectKey, setAspectKey] = useState(DEFAULT_ASPECT_KEY);
  const [caption, setCaption] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const selectedAspect = getAspectPreset(aspectKey);
  const captionError = captionContactError(caption);
  const allImagesCropped =
    croppedImages.length > 0 &&
    croppedImages.length === sourceImages.length &&
    croppedImages.every(Boolean);

  useEffect(() => {
    let cancelled = false;
    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        const res = await axios.get(`${API}/products`, {
          params: {
            page: 1,
            per_page: 100,
            status: "active,deactivated,draft",
          },
        });
        if (!cancelled) setProducts(res.data.items || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    };

    loadProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  const validateImageFile = useCallback(async (file) => {
    if (!IMAGE_ACCEPT_LIST.includes(file.type)) {
      throw new Error(`${file.name} isn't supported. Use JPG, PNG, or WebP.`);
    }
    if (file.size > IMAGE_MAX_BYTES) {
      throw new Error(
        `${file.name} is too large. Each image must be ${formatMb(IMAGE_MAX_BYTES)} MB or less.`,
      );
    }

    const dataUrl = await readFileAsDataUrl(file);
    const dimensions = await loadImageDimensions(dataUrl);
    if (
      dimensions.width < IMAGE_MIN.width ||
      dimensions.height < IMAGE_MIN.height
    ) {
      throw new Error(
        `${file.name} is too small. Use at least ${IMAGE_MIN.width}×${IMAGE_MIN.height} px.`,
      );
    }

    const ratio = dimensions.width / dimensions.height;
    if (ratio < MIN_ASPECT_RATIO || ratio > MAX_ASPECT_RATIO) {
      throw new Error(
        `${file.name} has an unsupported shape. Use images between 4:5 portrait and 1.91:1 landscape.`,
      );
    }

    return {
      id: `${file.name}-${file.lastModified}-${file.size}`,
      name: file.name,
      dataUrl,
      ...dimensions,
    };
  }, []);

  const pickImages = useCallback(async (append = false) => {
    const files = await new Promise((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = IMAGE_ACCEPT;
      input.multiple = true;
      input.onchange = (e) => resolve(Array.from(e.target.files || []));
      input.click();
    });

    if (!files.length) return;
    const currentCount = append ? sourceImages.length : 0;
    if (currentCount >= MAX_IMAGES_PER_POST) {
      toast.error(tr("Image limit reached"), {
        description: tr("You can add up to {{value0}} images in one post.", { value0: MAX_IMAGES_PER_POST }),
      });
      return;
    }
    if (currentCount + files.length > MAX_IMAGES_PER_POST) {
      toast.error(tr("Too many images"), {
        description: tr("You can add up to {{value0}} images in one post.", { value0: MAX_IMAGES_PER_POST }),
      });
      return;
    }

    try {
      const validated = [];
      for (const file of files) {
        validated.push(await validateImageFile(file));
      }
      if (append) {
        setSourceImages((prev) => [...prev, ...validated]);
        setCropper({ open: true, index: currentCount, recrop: false });
      } else {
        setSourceImages(validated);
        setCroppedImages([]);
        setAspectKey(DEFAULT_ASPECT_KEY);
        setCropper({ open: true, index: 0, recrop: false });
      }
    } catch (err) {
      toast.error(tr("Image can't be used"), {
        description: err?.message || "Please choose another image.",
      });
    }
  }, [sourceImages.length, validateImageFile]);

  const onCropApply = (dataUrl) => {
    setCroppedImages((prev) => {
      const next = [...prev];
      next[cropper.index] = dataUrl;
      return next;
    });

    if (cropper.recrop) {
      setCropper({ open: false, index: 0, recrop: false });
    } else if (cropper.index < sourceImages.length - 1) {
      setCropper((prev) => ({ open: true, index: prev.index + 1, recrop: false }));
    } else {
      setCropper({ open: false, index: 0, recrop: false });
    }
  };

  const reset = () => {
    setCropper({ open: false, index: 0, recrop: false });
    setSourceImages([]);
    setCroppedImages([]);
    setAspectKey(DEFAULT_ASPECT_KEY);
    setCaption("");
    setSelectedProductId("");
  };

  const removeCroppedImage = (index) => {
    const nextCropped = croppedImages.filter((_, i) => i !== index);
    const nextSources = sourceImages.filter((_, i) => i !== index);
    setCroppedImages(nextCropped);
    setSourceImages(nextSources);
    if (nextCropped.length === 0) {
      setAspectKey(DEFAULT_ASPECT_KEY);
    }
  };

  const recropImage = (index) => {
    setCropper({ open: true, index, recrop: true });
  };

  const submit = async () => {
    if (!allImagesCropped) {
      toast.error(tr("Add at least one image first"));
      return;
    }
    if (caption.length > CAPTION_MAX) return;
    if (captionError) {
      toast.error(tr("Caption needs a quick edit"), {
        description: captionError,
      });
      return;
    }
    setPosting(true);
    try {
      const keys = await Promise.all(croppedImages.map(image => uploadSellerImage(image, "gallery")));
      const res = await axios.post(`${API}/shops/me/posts`, {
        image: keys[0],
        images: keys,
        aspect_ratio: String(selectedAspect.value),
        aspect_preset: aspectKey,
        product_id: selectedProductId || null,
        caption: caption.trim(),
      });
      toast.success(tr("Posted to your gallery"));
      onPosted({
        ...(res.data.post || {}),
        image: res.data.post.image,
        images: res.data.post.images,
        image_count: croppedImages.length,
        aspect_ratio: selectedAspect.value,
        aspect_preset: aspectKey,
        caption: caption.trim(),
        product_id: selectedProductId || null,
        product_title:
          products.find((product) => product.id === selectedProductId)?.title ||
          res.data.post?.product_title ||
          null,
      });
      reset();
    } catch (err) {
      toast.error(tr("Couldn't post"), {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setPosting(false);
    }
  };

  return (
    <section
      data-testid="gallery-composer"
      className="rounded-2xl border border-[#EAECEF] bg-white p-5 sm:p-6"
    >
      <h2 className="text-base font-semibold text-[#0F1115]">{tr("Share a new post")}</h2>
      <p className="mt-1 text-xs text-[#6B7280]">{tr("Add up to ")}{MAX_IMAGES_PER_POST}{tr(" JPG, PNG, or WebP images · min ")}{IMAGE_MIN.width}×{IMAGE_MIN.height}{tr(" px · max ")}{formatMb(IMAGE_MAX_BYTES)}{tr(" MB each · crop once for the whole post")}</p>

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,420px)_1fr]">
        {/* Image slot */}
        <div>
          {croppedImages.length ? (
            <div className="space-y-3">
              <div
                className="relative w-full overflow-hidden rounded-xl border border-[#EAECEF] bg-[#F3EEEE]"
                style={{ aspectRatio: selectedAspect.value }}
              >
                <ResponsiveImage imageWidth={960}
                  src={croppedImages[0]}
                  alt={tr("Selected cover")}
                  className="h-full w-full object-cover"
                />
                <div className="absolute start-2 top-2 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-[#0F1115] shadow-sm">
                  {tr("Images: {{count}}", { count: croppedImages.length })} · {selectedAspect.display}
                </div>
                <div className="absolute inset-x-2 bottom-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="composer-replace-image"
                    onClick={() => pickImages(false)}
                    className="inline-flex h-8 items-center justify-center rounded-md bg-white/95 text-xs font-semibold text-[#0F1115] shadow-sm backdrop-blur transition-colors hover:bg-white"
                  >{tr("Replace deck")}</button>
                  <button
                    type="button"
                    data-testid="composer-add-more-images"
                    onClick={() => pickImages(true)}
                    disabled={croppedImages.length >= MAX_IMAGES_PER_POST}
                    className="inline-flex h-8 items-center justify-center rounded-md bg-white/95 text-xs font-semibold text-[#0F1115] shadow-sm backdrop-blur transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                  >{tr("Add more")}</button>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-2 sm:grid-cols-6">
                {croppedImages.map((img, index) => (
                  <div
                    key={sourceImages[index]?.id || index}
                    className="group relative overflow-hidden rounded-lg border border-[#EAECEF] bg-[#F3EEEE]"
                    style={{ aspectRatio: selectedAspect.value }}
                  >
                    <ResponsiveImage imageWidth={960}
                      src={img}
                      alt={tr("Cropped {{value0}}", { value0: index + 1 })}
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => recropImage(index)}
                      className="absolute inset-0 hidden items-center justify-center bg-black/35 text-[11px] font-semibold text-white group-hover:flex"
                    >{tr("Crop")}</button>
                    <button
                      type="button"
                      aria-label={tr("Remove image")}
                      onClick={() => removeCroppedImage(index)}
                      className="absolute end-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#9B0000] shadow-sm"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <button
              type="button"
              data-testid="composer-pick-image"
              onClick={() => pickImages(false)}
              className="group flex min-h-[260px] w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#D7DAE0] bg-[#FAFAFB] px-5 text-center text-[#6B7280] transition-colors hover:border-[#FF5757] hover:text-[#FF5757]"
            >
              <ImagePlus className="h-8 w-8" strokeWidth={1.4} />
              <span className="mt-2 text-sm font-semibold">{tr("Add images")}</span>
              <span className="mt-1 max-w-[260px] text-[11px] text-[#9BA1AB] group-hover:text-[#FF5757]/80">{tr("Select 1-")}{MAX_IMAGES_PER_POST}{tr(" images. You'll crop each image before posting.")}</span>
            </button>
          )}
        </div>

        {/* Caption + actions */}
        <div className="flex flex-col">
          <label className="mb-4 block">
            <span className="text-sm font-semibold text-[#0F1115]">{tr("Link your product")}</span>
            <select
              value={selectedProductId}
              onChange={(event) => setSelectedProductId(event.target.value)}
              disabled={productsLoading || products.length === 0}
              data-testid="composer-product-link"
              className="humrahe-input mt-1"
            >
              <option value="">
                {productsLoading
                  ? tr("Loading products...")
                  : products.length === 0
                    ? tr("No products available")
                    : tr("No linked product")}
              </option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.title}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-[#6B7280]">{tr("Optional. Link this post to one of your products so customers can connect the moment with the experience.")}</span>
          </label>

          <CaptionField
            value={caption}
            onChange={setCaption}
            error={captionError}
            testidPrefix="composer-"
          />

          <div className="mt-auto flex items-center justify-end gap-2 pt-5">
            {(croppedImages.length > 0 || caption) && (
              <button
                type="button"
                data-testid="composer-clear"
                onClick={reset}
                disabled={posting}
                className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] disabled:opacity-50"
              >{tr("Clear")}</button>
            )}
            <button
              type="button"
              data-testid="composer-post"
              onClick={submit}
              disabled={
                !allImagesCropped ||
                posting ||
                caption.length > CAPTION_MAX ||
                Boolean(captionError)
              }
              className="humrahe-btn-primary inline-flex h-10 items-center gap-2 rounded-md px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
            >
              {posting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {posting ? tr("Posting…") : tr("Post to gallery")}
            </button>
          </div>
        </div>
      </div>

      <ShopImageCropper
        open={cropper.open}
        imageSrc={sourceImages[cropper.index]?.dataUrl}
        aspect={selectedAspect.value}
        aspectOptions={ASPECT_PRESETS}
        selectedAspectKey={aspectKey}
        onAspectChange={setAspectKey}
        disableAspectOptions={cropper.index > 0 || croppedImages.some(Boolean)}
        cropShape="rect"
        title={tr("Crop image {{value0}} of {{value1}}", { value0: Math.min(cropper.index + 1, sourceImages.length || 1), value1: sourceImages.length || 1 })}
        helperText="Choose one format for the entire post. After the first crop, the format stays locked for the deck."
        preserveResolution
        outputSize={selectedAspect.outputSize}
        applyLabel={cropper.index < sourceImages.length - 1 ? "Apply & next" : "Apply"}
        onClose={() => setCropper({ open: false, index: 0, recrop: false })}
        onApply={onCropApply}
      />
    </section>
  );
};

/* ---------- Edit caption modal ---------- */
const EditCaptionModal = ({ post, onClose, onSaved }) => {
  usePartnerTranslation();
  const [caption, setCaption] = useState(post?.caption || "");
  const [selectedProductId, setSelectedProductId] = useState(post?.product_id || "");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const captionError = captionContactError(caption);

  useEffect(() => {
    setCaption(post?.caption || "");
    setSelectedProductId(post?.product_id || "");
  }, [post?.id, post?.caption, post?.product_id]);

  useEffect(() => {
    let cancelled = false;
    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        const res = await axios.get(`${API}/products`, {
          params: {
            page: 1,
            per_page: 100,
            status: "active,deactivated,draft",
          },
        });
        if (!cancelled) setProducts(res.data.items || []);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    };

    loadProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!post) return null;

  const initialProductId = post.product_id || "";
  const selectedProductMissing =
    selectedProductId &&
    !products.some((product) => product.id === selectedProductId);
  const hasChanges =
    caption !== (post.caption || "") || selectedProductId !== initialProductId;

  const submit = async () => {
    if (caption.length > CAPTION_MAX) return;
    if (captionError) {
      toast.error(tr("Caption needs a quick edit"), {
        description: captionError,
      });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/shops/me/posts/${post.id}`, {
        caption: caption.trim(),
        product_id: selectedProductId || null,
      });
      toast.success(tr("Post updated"));
      onSaved(res.data.post);
    } catch (err) {
      toast.error(tr("Couldn't save post"), {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="edit-caption-modal"
        className="max-w-[620px] rounded-2xl border-[#EFE4E4] p-0"
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-6 py-4">
          <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Edit gallery post")}</DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280]">{tr("Changes show on your public shop gallery immediately.")}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4 px-6 py-5 sm:grid-cols-[200px_1fr]">
          <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-[#EAECEF]">
            <ResponsiveImage imageWidth={960}
              src={post.image}
              alt={tr("Post")}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-4 block">
              <span className="text-sm font-semibold text-[#0F1115]">{tr("Link your product")}</span>
              <select
                value={selectedProductId}
                onChange={(event) => setSelectedProductId(event.target.value)}
                disabled={productsLoading}
                data-testid="edit-product-link"
                className="humrahe-input mt-1"
              >
                <option value="">
                  {productsLoading
                    ? tr("Loading products...")
                    : products.length === 0
                      ? tr("No products available")
                      : tr("No linked product")}
                </option>
                {selectedProductMissing && (
                  <option value={selectedProductId}>
                    {post.product_title || tr("Current linked product")}
                  </option>
                )}
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.title}
                  </option>
                ))}
              </select>
            </label>
            <CaptionField
              value={caption}
              onChange={setCaption}
              error={captionError}
              testidPrefix="edit-"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[#EFE4E4] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="edit-cancel"
            className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
          >{tr("Cancel")}</button>
          <button
            type="button"
            data-testid="edit-save"
            onClick={submit}
            disabled={saving || !hasChanges || Boolean(captionError)}
            className="humrahe-btn-primary inline-flex h-10 items-center rounded-md px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? tr("Saving…") : tr("Save changes")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Delete confirm dialog ---------- */
const DeleteConfirmModal = ({ post, onClose, onDeleted }) => {
  usePartnerTranslation();
  const [deleting, setDeleting] = useState(false);

  if (!post) return null;

  const submit = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/shops/me/posts/${post.id}`);
      toast.success(tr("Post deleted"));
      onDeleted(post.id);
    } catch (err) {
      toast.error(tr("Couldn't delete"), {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="delete-confirm-modal"
        className="max-w-[440px] rounded-2xl border-[#EFE4E4] p-0"
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-6 py-4">
          <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">{tr("Delete this post?")}</DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280]">{tr("This removes the photo and caption from your public gallery. It can't be undone.")}</DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-end gap-2 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="delete-cancel"
            className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
          >{tr("Keep post")}</button>
          <button
            type="button"
            data-testid="delete-confirm"
            onClick={submit}
            disabled={deleting}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[#9B0000] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" />
            {deleting ? tr("Deleting…") : tr("Delete post")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Photo viewer ---------- */
const PhotoViewerModal = ({ post, onClose }) => {
  usePartnerTranslation();
  const [index, setIndex] = useState(0);
  const images = useMemo(() => getPostImages(post), [post]);
  const count = getPostImageCount(post);
  const canNavigate = images.length > 1;
  const aspectRatio = Number(post?.aspect_ratio) || 4 / 3;

  useEffect(() => {
    setIndex(0);
  }, [post?.id]);

  if (!post || count === 0) return null;

  const goPrev = () => {
    setIndex((current) => (current === 0 ? images.length - 1 : current - 1));
  };

  const goNext = () => {
    setIndex((current) => (current === images.length - 1 ? 0 : current + 1));
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="photo-viewer-modal"
        className="max-h-[92vh] w-[calc(100vw-24px)] max-w-[980px] overflow-hidden rounded-2xl border-[#EFE4E4] p-0"
      >
        <div className="grid max-h-[92vh] grid-cols-1 bg-white md:grid-cols-[minmax(0,1fr)_320px]">
          <div className="relative flex min-h-[320px] items-center justify-center bg-[#0F1115] sm:min-h-[480px]">
            <div
              className="w-full max-w-[min(100%,680px)] overflow-hidden bg-black"
              style={{ aspectRatio }}
            >
              <ResponsiveImage imageWidth={960}
                src={images[index]}
                alt={tr("Gallery post image {{value0}}", { value0: index + 1 })}
                className="h-full w-full object-contain"
              />
            </div>

            {canNavigate && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label={tr("Previous image")}
                  data-testid="photo-viewer-prev"
                  className="absolute start-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/92 text-[#0F1115] shadow-sm transition-colors hover:bg-white"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label={tr("Next image")}
                  data-testid="photo-viewer-next"
                  className="absolute end-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/92 text-[#0F1115] shadow-sm transition-colors hover:bg-white"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/45 px-3 py-1.5">
                  {images.map((_, dotIndex) => (
                    <button
                      key={dotIndex}
                      type="button"
                      onClick={() => setIndex(dotIndex)}
                      aria-label={tr("View image {{value0}}", { value0: dotIndex + 1 })}
                      className={cn(
                        "h-1.5 rounded-full transition-all",
                        dotIndex === index
                          ? "w-5 bg-white"
                          : "w-1.5 bg-white/55 hover:bg-white/80",
                      )}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <aside className="flex max-h-[42vh] flex-col overflow-y-auto border-t border-[#EFE4E4] p-5 md:max-h-none md:border-l md:border-t-0">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-extrabold text-[#0F1115]">{tr("Gallery post")}</h3>
                <p className="text-xs text-[#6B7280]">
                  {canNavigate ? tr("{{value0}} of {{value1}} images", { value0: index + 1, value1: images.length }) : tr("1 image")}
                </p>
              </div>
              {canNavigate && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[#F3EEEE] px-2.5 py-1 text-xs font-semibold text-[#0F1115]">
                  <Images className="h-3.5 w-3.5" />{tr("Deck")}</span>
              )}
            </div>

            {post.caption ? (
              <p className="mt-5 whitespace-pre-wrap break-words text-sm leading-6 text-[#0F1115]">
                {renderCaption(post.caption)}
              </p>
            ) : (
              <p className="mt-5 text-sm italic text-[#9BA1AB]">{tr("No caption")}</p>
            )}

            {post.product_title && (
              <p
                data-testid="photo-viewer-product"
                className="mt-4 rounded-lg border border-[#EAECEF] bg-[#FAFAFB] px-3 py-2 text-xs font-semibold text-[#0F1115]"
              >{tr("Linked product: ")}{post.product_title}
              </p>
            )}

            <p className="mt-5 text-xs text-[#9BA1AB]">
              {new Date(post.created_at).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ---------- Single post card ---------- */
const PostCard = ({ post, onEdit, onDelete, onView }) => {
  usePartnerTranslation();
  const images = getPostImages(post);
  const imageCount = getPostImageCount(post);
  const openPost = () => onView(post);
  const onCardKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPost();
    }
  };

  return (
    <article
      data-testid={`post-card-${post.id}`}
      role="button"
      tabIndex={0}
      onClick={openPost}
      onKeyDown={onCardKeyDown}
      className="group relative cursor-pointer overflow-hidden rounded-lg bg-[#F3EEEE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5757]/40"
    >
      <ResponsiveImage imageWidth={960}
        src={images[0] || post.image}
        alt={post.caption || tr("Gallery post")}
        className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        loading="lazy"
      />
      <div className="pointer-events-none absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/10" />
      {imageCount > 1 && (
        <div
          className="pointer-events-none absolute end-2 top-2 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md bg-black/65 text-white shadow-sm ring-1 ring-white/25 backdrop-blur"
          aria-label={tr("{{value0}} images", { value0: imageCount })}
          title={tr("{{value0}} images", { value0: imageCount })}
        >
          <Images className="h-4 w-4" strokeWidth={2.2} />
        </div>
      )}
      {post.product_title && (
        <div className="pointer-events-none absolute bottom-2 start-2 z-10 max-w-[calc(100%-1rem)] rounded-md bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-[#0F1115] shadow-sm">
          {post.product_title}
        </div>
      )}
      <div className="absolute start-2 top-2 z-10 flex items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100 group-focus-within:opacity-100">
        <button
          type="button"
          data-testid={`post-edit-${post.id}`}
          onClick={(event) => {
            event.stopPropagation();
            onEdit(post);
          }}
          aria-label={tr("Edit caption")}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/95 text-[#0F1115] shadow-sm backdrop-blur transition-colors hover:bg-white"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          data-testid={`post-delete-${post.id}`}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(post);
          }}
          aria-label={tr("Delete post")}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-white/95 text-[#9B0000] shadow-sm backdrop-blur transition-colors hover:bg-white"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
  </article>
  );
};

/* ---------- Main page ---------- */
export default function UploadGalleryPage() {
  usePartnerTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [deletingPost, setDeletingPost] = useState(null);
  const [viewingPost, setViewingPost] = useState(null);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shops/me/posts`);
      setPosts(res.data.items || []);
    } catch (err) {
      toast.error(tr("Couldn't load your gallery"), {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handlePosted = (post) => {
    setPosts((prev) => [post, ...prev]);
  };

  const handleSaved = (updated) => {
    setPosts((prev) =>
      prev.map((p) => (p.id === updated.id ? updated : p)),
    );
    setEditingPost(null);
  };

  const handleDeleted = (id) => {
    setPosts((prev) => prev.filter((p) => p.id !== id));
    setDeletingPost(null);
  };

  return (
    <main
      data-testid="upload-gallery-page"
      className="mx-auto w-full max-w-[1100px] flex-1 px-4 py-10 sm:px-6 md:px-10"
    >
      <header>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0F1115] md:text-4xl">{tr("Upload in gallery")}</h1>
        <p className="mt-2 max-w-xl text-sm text-[#6B7280]">{tr("Share your best moments. They appear on your public gallery for customers to explore.")}</p>
      </header>

      <div className="mt-8">
        <UploadComposer onPosted={handlePosted} />
      </div>

      <section className="mt-12">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-base font-semibold text-[#0F1115]">{tr("Your posts")}</h2>
            <p className="text-xs text-[#6B7280]">
              {loading
                ? tr("Loading…")
                : `${posts.length} ${posts.length === 1 ? "post" : "posts"}`}
            </p>
          </div>
        </div>

        <div className="mt-4">
          {loading && posts.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-[#FF5757]" />
            </div>
          ) : posts.length === 0 ? (
            <div
              data-testid="gallery-empty"
              className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#EAECEF] bg-[#FAFAFB] px-6 py-16 text-center"
            >
              <ImagePlus className="h-12 w-12 text-[#CFD2D8]" strokeWidth={1.3} />
              <p className="mt-4 text-lg font-semibold text-[#0F1115]">{tr("No posts yet")}</p>
              <p className="mt-1 max-w-md text-sm text-[#6B7280]">{tr("Upload your first photo above. It'll show on your public shop gallery instantly.")}</p>
            </div>
          ) : (
            <div
              data-testid="posts-grid"
              className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:gap-4"
            >
              {posts.map((p) => (
                <PostCard
                  key={p.id}
                  post={p}
                  onEdit={setEditingPost}
                  onDelete={setDeletingPost}
                  onView={setViewingPost}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {editingPost && (
        <EditCaptionModal
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSaved={handleSaved}
        />
      )}
      {deletingPost && (
        <DeleteConfirmModal
          post={deletingPost}
          onClose={() => setDeletingPost(null)}
          onDeleted={handleDeleted}
        />
      )}
      {viewingPost && (
        <PhotoViewerModal
          post={viewingPost}
          onClose={() => setViewingPost(null)}
        />
      )}
    </main>
  );
}
